/* eslint-disable react/no-unknown-property */
import React, { useState, useEffect, useCallback } from 'react'
import { AxiosRequestConfig } from 'axios'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import {
  HStack,
  Text,
  Box,
  FormControl,
  InputGroup,
  Select,
  FormLabel,
  Input,
  FormErrorMessage,
  VStack,
  FormHelperText,
  Button,
  useDisclosure,
} from '@chakra-ui/react'

import { removePlusSign } from 'utils/functions/formatNumber'
import IBalert from 'components/common/IBalert/IBalert'
import LoadingScreen from 'components/common/LoadingScreen/LoadingScreen'
import IBModalAction from 'components/common/IBModalAction/IBModalAction'
import countriesMock from 'services/mock/countries.json'
import { postSubscriber, findInOb2Sib } from 'services/api'
import settingValidate from 'utils/validation/settingValidate'
import { isInternalErrorStatus, isSuccessStatus, maxLengthcodArea, maxLengthNumber } from 'utils/functions/validations'
import { useAuthContext } from 'providers/AuthProvider'
import { IPaisList } from 'services/mock/interfaces/countries.d'
import { ISubscriberSave } from 'providers/ISubscriberProvider.d'
import { IFetchedFindInObSib, IResponseGeneric } from 'services/IApi.d'
import { IUser } from './LobbyPage.d'

const borderStyle = 'var(--chakra-radii-md) !important'
const countries: IPaisList[] = countriesMock.data.paisList.filter(pais => pais.paisCodTel)

const LobbyPage = () => {
  const navigate = useNavigate()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [userProfile, setUserProfile] = useState<any>()
  const [provisionalToken, setProvisionalToken] = useState<string>('')
  const [countryFlag, setCountryFlag] = useState<string>('ar')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { getProfile, logout } = useAuthContext()
  const [isButtonLoad, setIsButtonLoad] = useState<boolean>(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenLogout, onOpen: onOpenLogout, onClose: onCloseLogout } = useDisclosure()

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    getValues,
    watch,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<IUser>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      documento: '',
      tipoDocumento: '',
      email: '',
      emailConfirm: '',
      codPais: '',
      phoneNumber: '',
      codArea: '',
      user: '',
      password: '',
    },
  })

  const ArgCodeDn: string = 'AR'
  const codPaisWatch: string = watch('codPais')
  const codAreaWatch: string = watch('codArea')
  const tipoDocumento: string = watch('tipoDocumento')
  const phoneNumberWatch: string = watch('phoneNumber')
  const typesDocuments: string[] = ['Doc.', 'Pas.']
  // eslint-disable-next-line no-nested-ternary
  const typeDocument: string = tipoDocumento === 'Doc.' ? 'Documento Extranjero' : tipoDocumento === 'Pas.' ? 'Pasaporte' : 'Tipo'
  const showtextifArg: string = codPaisWatch === ArgCodeDn ? 'Ingresá el código de área sin el 0 y el número sin el 15' : ''
  const showtextifDocument: string = tipoDocumento === '' ? 'Seleccioná un tipo de documento' : ''
  const msgValidatePhone: string = 'El formato del teléfono no es válido'
  const msgEmailConfirm: string = 'Los emails no coinciden'
  const limitPhoneNumber: number = 14
  const isDisabledButton = (): boolean => !isValid || isButtonLoad
  const [isCuil, setIsCuil] = useState<boolean>(false)
  // eslint-disable-next-line no-useless-escape
  const getCountryByVal = (value: string): IPaisList => countries.find(country => country.codDN === value || country.paisCodTel === value)

  const handleReCaptchaVerify = useCallback(async (action: string) => {
    if (!executeRecaptcha) {
      console.info('Execute recaptcha not yet available')
      return ''
    }

    const token = await executeRecaptcha(action)
    return token
  }, [executeRecaptcha])

  const resetValidation = e => {
    clearErrors([e.target.name])
  }

  const onSubmit = async (data: IUser) => {
    setIsButtonLoad(true)
    const docType = userProfile.pais === ArgCodeDn ? 'Documento' : data.tipoDocumento
    const docTypeFormatted = docType === typesDocuments[1] ? 'PAS' : 'Documento'

    const subscriber: ISubscriberSave = {
      documento: {
        tipo: docTypeFormatted,
        numero: userProfile.documento,
        nacionalidad: getCountryByVal(userProfile.pais).nombre,
      },
      nombre: userProfile.nombre.toLowerCase(),
      apellido: userProfile.apellido.toLowerCase(),
      credenciales: {
        usuario: null,
        password: null,
      },
      email: data.email,
      telefono: {
        codPais: removePlusSign(data.codPais),
        codArea: data.codArea,
        numero: Number(removePlusSign(data.phoneNumber)),
      },
      codNacionalidad: userProfile.pais,
      isSibUser: true,
    }

    const recaptchaToken: string = await executeRecaptcha('subscriptor')
    const options: AxiosRequestConfig<unknown> = {
      headers: {
        token: provisionalToken,
        Authorization: `Bearer ${provisionalToken}`,
        'x-recaptcha-token': recaptchaToken,
      }
    }

    const response: IResponseGeneric = await postSubscriber(subscriber, options)
    if (!isSuccessStatus(response.status)) {
      setIsButtonLoad(false)
      onOpen()
      return
    }
    navigate('/dashboard', { state: 'successSibuser' })
  }

  // eslint-disable-next-line no-unsafe-optional-chaining
  const countryPhoneCodeLength: number = +getCountryByVal(codPaisWatch)?.paisCodTel.length || 0
  const countryCodAreaLength: number = codAreaWatch.length
  const phoneLength: number = phoneNumberWatch.length

  const charCodAreaPhoneLength = countryCodAreaLength + phoneLength
  const returnMaxPhone = (): number => {
    const countryCodeLength = getValues('codPais').length
    const areaCodeLength = getValues('codArea').length
    const phoneNumberLength = getValues('phoneNumber').length
    return countryCodeLength + areaCodeLength + phoneNumberLength
  }

  const handleChange = (e, field) => {
    const valueField = e.target.value
    if (field === 'codPais') {
      setCountryFlag(getCountryByVal(valueField)?.codDN?.toLowerCase())
      const limitChar = charCodAreaPhoneLength + countryPhoneCodeLength
      if (limitChar > 14) {
        setError(field, { type: 'manual', message: msgValidatePhone })
      } else {
        clearErrors(['codPais', 'codArea', 'phoneNumber'])
      }
      setValue('codArea', '', { shouldValidate: false })
      setValue('phoneNumber', '', { shouldValidate: false })
    }
  }



  const existInOb2 = async () => {
    try {
      const recaptchaToken: string = await handleReCaptchaVerify('lobby')
      const response: IFetchedFindInObSib = await findInOb2Sib({
        apellido: userProfile.apellido,
        documento: userProfile.documento,
        nacionalidad: getCountryByVal(userProfile.pais).nombre,
        nombre: userProfile.nombre,
        pais: userProfile.pais,
      }, recaptchaToken)
      if (isInternalErrorStatus(response.status)) {
        return navigate('/suscripcion/error-suscripcion')
      }
      setProvisionalToken(response.data.token)
      
      return response.data.existeOb2
    } catch (e) {
      throw new Error(`Error: ${e}`)
    }
  }

  const modal = {
    title: 'Estás por cerrar sesión',
    bodyModal: '¿Estás seguro que deseás cerrar sesión?',
    footerModal: {
      cancel: 'Cancelar',
      closeSessionText: 'Sí, cerrar sesión',
      closeSession: async () => {
        logout()
      },
    },
  }

  useEffect(() => {
    if (!userProfile || !executeRecaptcha) return

    existInOb2().then(isInOb2 => {
      if (!isInOb2) {
        setIsCuil(userProfile.pais === ArgCodeDn)
        setValue('codPais', getCountryByVal(userProfile.pais).paisCodTel, { shouldValidate: false })
        setValue('documento', userProfile.documento, { shouldValidate: false })
        setCountryFlag(getCountryByVal(userProfile.pais)?.codDN.toLocaleLowerCase())
        setIsLoading(false)
      } else {
        navigate('/dashboard')
      }
    })
  }, [userProfile, executeRecaptcha])

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (getProfile()) {
      setUserProfile(getProfile())
    } else {
      timer = setTimeout(() => {
        if (getProfile()) {
          setUserProfile(getProfile())
          clearTimeout(timer)
        }
      }, 1000)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [])

  return (
    <Box>
      {isLoading ? (
        <LoadingScreen title='Estamos procesando tu información' description='Esto puede demorar unos instantes' fullpage />
      ) : (
        <Box className='formContent' maxW='350px' margin='24px auto'>
          <Text textAlign='center' className='title' fontSize='3xl' fontWeight='bold'>
            Completá los datos
          </Text>
          <Text className='sub-title' fontSize='lg' textAlign='center'>
            Necesitamos información adicional para que puedas agregar empresas.
          </Text>
          <form onSubmit={handleSubmit(onSubmit)} data-testid='form-lobby' className='form-lobby'>
            <HStack spacing='24px' mt='40px' pb='45px' justifyContent='center'>
              <Box w='350px'>
                <VStack spacing='12'>
                  {!isCuil && (
                    <FormControl
                      isInvalid={!!errors.tipoDocumento || !!errors.documento}
                      className={`float ${watch('tipoDocumento') ? 'hasValue' : ''}`}
                    >
                      <InputGroup>
                        <Select
                          background='white'
                          roundedRight='0'
                          maxW='90px'
                          data-testid='tipoDocumento'
                          {...register('tipoDocumento', {
                            ...settingValidate.tipoDocumento,
                            onChange: e => {
                              handleChange(e, 'tipoDocumento')
                            },
                          })}
                        >
                          {typesDocuments.map(document => (
                            <option value={document} key={document}>
                              {document}
                            </option>
                          ))}
                        </Select>
                        <FormLabel>{typeDocument}</FormLabel>
                        <Input
                          type='text'
                          disabled
                          roundedLeft='0'
                          onFocus={e => resetValidation(e)}
                          placeholder='Número'
                          data-testid='documento'
                          {...register('documento', {
                            ...settingValidate.documento,
                          })}
                        />
                      </InputGroup>
                      <FormErrorMessage>{errors?.documento && errors.documento?.message}</FormErrorMessage>
                      <FormHelperText position='absolute'>{showtextifDocument}</FormHelperText>
                    </FormControl>
                  )}
                  <FormControl isInvalid={!!errors.email}>
                    <Input
                      background='white'
                      type='text'
                      placeholder=' '
                      data-testid='email'
                      maxLength={80}
                      {...register('email', {
                        required: 'Este campo es obligatorio',
                        pattern: {
                          value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/,
                          message: 'El formato del email no es válido',
                        },
                      })}
                    />
                    <FormLabel>Email</FormLabel>
                    <FormErrorMessage>{errors?.email && errors.email?.message}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.emailConfirm}>
                    <Input
                      background='white'
                      type='text'
                      placeholder=' '
                      data-testid='emailConfirm'
                      maxLength={80}
                      {...register('emailConfirm', {
                        validate: value => value === getValues('email') || msgEmailConfirm,
                        onChange: e => {
                          handleChange(e, 'emailConfirm')
                        },
                      })}
                    />
                    <FormLabel>Confirmar Email</FormLabel>
                    <FormErrorMessage>{errors?.emailConfirm && errors.emailConfirm?.message}</FormErrorMessage>
                  </FormControl>
                  <FormControl
                    isInvalid={!!errors.codPais || !!errors.codArea || !!errors.phoneNumber}
                    className={`float ${watch('codPais') ? 'hasValue' : ''}`}
                  >
                    <InputGroup>
                      <Box
                        data-testid='areaCodeFlag'
                        position='absolute'
                        left={2}
                        height='100%'
                        display='inline-flex'
                        justifyContent='center'
                        alignItems='center'
                        pointerEvents='none'
                        zIndex={1}
                        top='50%'
                        transform='translateY(-50%)'
                      >
                        <span className={`flag flag-${countryFlag}`} />
                      </Box>
                      <Select
                        variant='flag'
                        background='white'
                        borderRadius={borderStyle}
                        data-testid='codPais'
                        {...register('codPais', {
                          ...settingValidate.codPais,
                          onChange: e => {
                            handleChange(e, 'codPais')
                          },
                        })}
                      >
                        {countries.map(({ codDN, paisCodTel }: IPaisList) => (
                          <option value={paisCodTel} key={codDN}>
                            +{paisCodTel}
                          </option>
                        ))}
                      </Select>
                      <FormLabel>Teléfono</FormLabel>
                      <Input
                        type='text'
                        background='white'
                        minW='70px'
                        maxW='90px'
                        onFocus={e => resetValidation(e)}
                        maxLength={maxLengthcodArea(phoneLength, countryPhoneCodeLength, countryCodAreaLength)}
                        placeholder='Código'
                        data-testid='codArea'
                        borderRadius={borderStyle}
                        mx={2}
                        w='60%'
                        {...register('codArea', {
                          ...settingValidate.codArea,
                          validate: () => (returnMaxPhone() <= limitPhoneNumber) || msgValidatePhone,
                          onChange: e => {
                            handleChange(e, 'codArea')
                          },
                        })}
                      />
                      <Input
                        type='text'
                        minW='130px'
                        background='white'
                        maxW='130px'
                        onFocus={e => resetValidation(e)}
                        maxLength={maxLengthNumber(countryPhoneCodeLength, countryCodAreaLength)}
                        placeholder='Número'
                        data-testid='phoneNumber'
                        borderRadius={borderStyle}
                        {...register('phoneNumber', {
                          ...settingValidate.phoneNumber,
                          validate: () => returnMaxPhone() <= limitPhoneNumber || msgValidatePhone,
                          onChange: e => {
                            handleChange(e, 'phoneNumber')
                          },
                        })}
                      />
                    </InputGroup>
                    {errors?.phoneNumber || errors?.codArea || errors?.codPais ? (
                      <FormErrorMessage>
                        {errors.phoneNumber?.message || errors.codArea?.message || errors.codPais?.message}
                      </FormErrorMessage>
                    ) : (
                      <FormHelperText position='absolute'>{showtextifArg}</FormHelperText>
                    )}
                  </FormControl>
                </VStack>
              </Box>
            </HStack>

            <HStack justifyContent='center' pb='25px' pt='35px'>
              <VStack spacing='5'>
                <Button data-testid='agregar' type='submit' w='158px' disabled={isDisabledButton()} isLoading={isButtonLoad}>
                  Continuar
                </Button>
                <Button variant='primary-text' onClick={onOpenLogout}>
                  Cerrar sesión
                </Button>
              </VStack>
            </HStack>
          </form>
          <IBalert
            type='error'
            isOpen={isOpen}
            onClose={onClose}
            title='No podemos continuar'
            text='Estamos trabajando para solucionarlo, volvé a intentar más tarde.'
            buttonClose
          />
          <IBModalAction
            isOpen={isOpenLogout}
            onClose={onCloseLogout}
            buttonActionText={modal?.footerModal.closeSessionText}
            buttonAction={modal?.footerModal.closeSession}
            buttonCancel={modal?.footerModal.cancel}
            title={modal?.title}
            textBody={modal?.bodyModal}
          />
        </Box>
      )}
    </Box>
  )
}

export default LobbyPage
